import React, { useRef, useEffect, useState, useContext } from 'react'
import { graphql } from 'gatsby'
import { RichText } from 'prismic-reactjs'
import ModalSide from '../components/ModalSide'
import Button from '../components/Button'
import SplitText from '../components/SplitText'
import smoothScrollTo from '../helpers/smoothScrollTo'
import clamp from '../helpers/clamp'
import { ContextLoader } from '../context';
import { detectElementsOnScreen, killObservers } from '../helpers/detectElementsOnScreen'
import VideoResponsive from '../components/VideoResponsive'
import video1 from '../videos/1.mp4'
import video2 from '../videos/2.mp4'
import video3 from '../videos/3.mp4'
import video4 from '../videos/4.mp4'
import video5 from '../videos/5.mp4'
import { adaptBgColorToVideo } from '../helpers/adaptBgColorToVideo'
import ColorWitnessImage from '../images/background-color-witness-tech.png';
import { tabletP } from '../utils/bp'
import Seo from '../components/Seo'

const Technology = ({ data, location }) => {
	const pageData = data.prismicTechnology.data

	useEffect(() => {
		setTimeout(() => {
			adaptBgColorToVideo('.ColorWitnessImageTech', '--techColor')
		}, 500)
	}, [])

	const [isLoading] = useContext(ContextLoader);
	useEffect(() => {
		if (isLoading === true) return
		const [observers, elements] = detectElementsOnScreen()
		return () => {
			killObservers(observers, elements)
		}
	}, [isLoading])

	/**
	*	Handle scroll
	*/
	const wh = useRef(null)
	const isMobile = useRef(false)
	useEffect(() => {
		isMobile.current = window.innerWidth <= tabletP ? true : false
		const handleScroll = () => {
			const scrollY = window.scrollY
			toggleVideos(scrollY)
			if (!isMobile.current) {
				detectCurrentSection(scrollY)
				sectionsParallax(scrollY)
			}
		}
		const handleResize = () => {
			wh.current = window.innerHeight
		}
		handleResize()
		window.addEventListener('scroll', handleScroll)
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('scroll', handleScroll)
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	/**
	*	Hero and sections animations on scroll / parallax
	*/
	const heroBackgroundEl = useRef(null)
	const sectionsBackgroundsEls = useRef([])
	const sectionsOverlays = useRef([])
	const sectionsParallax = scrollY => {
		const heroBackgroundProgress = clamp(scrollY / wh.current, 0, 1)
		heroBackgroundEl.current.style.transform = `scale(${1 + 0.1 * heroBackgroundProgress})`
		heroBackgroundEl.current.style.opacity = 1 - heroBackgroundProgress * 0.8

		sectionsBackgroundsEls.current.map((backgroundEl, index) => {
			const translationProgress = (clamp(((scrollY - wh.current * (index + 1)) / wh.current), -1, 1) + 1) * 0.5
			backgroundEl.style.transform = `scale(${1 + 0.5 * translationProgress})`
			const opacityProgress = clamp(((scrollY - wh.current * (index + 1)) / wh.current), 0, 1)
			sectionsOverlays.current[index].style.opacity = opacityProgress
		})
	}

	/**
	*	Toggle videos state
	*/
	const [videosArePlaying, setVideosArePlaying] = useState([false, false, false, false, false])
	const videosArePlayingRef = useRef([false, false, false, false, false])
	const toggleVideos = (scrollY) => {
		videosArePlaying.forEach((video, index) => {
			const startY = isMobile.current === false ? wh.current * index : (wh.current * 2) * index + wh.current
			const endY = startY + wh.current * 2
			if (scrollY > startY && scrollY < endY && videosArePlayingRef.current[index] === false) {
				const newArr = [...videosArePlayingRef.current]
				newArr[index] = true
				setVideosArePlaying(newArr)
				videosArePlayingRef.current = newArr
			} else if ((scrollY <= startY || scrollY > endY) && videosArePlayingRef.current[index] === true) {
				const newArr = [...videosArePlayingRef.current]
				newArr[index] = false
				setVideosArePlaying(newArr)
				videosArePlayingRef.current = newArr
			}
		})
	}

	/**
	*	Detect current section
	*/
	const currentSection = useRef(-1)
	const progressItemEls = useRef([])
	const [isProgressVisible, setIsProgressVisible] = useState(false)
	const detectCurrentSection = scrollY => {
		// Set current section
		if (scrollY > wh.current * 0.5 + wh.current * (currentSection.current + 1) && scrollY < 4800) {
			if (progressItemEls.current[currentSection.current]) {
				progressItemEls.current[currentSection.current].classList.remove('isCurrent')
			}
			currentSection.current += 1
			if (progressItemEls.current[currentSection.current]) {
				progressItemEls.current[currentSection.current].classList.add('isCurrent')
			}
		} else if (scrollY < wh.current * 0.5 + wh.current * currentSection.current && scrollY < 4800) {
			if (progressItemEls.current[currentSection.current]) {
				progressItemEls.current[currentSection.current].classList.remove('isCurrent')
			}
			currentSection.current -= 1
			if (progressItemEls.current[currentSection.current]) {
				progressItemEls.current[currentSection.current].classList.add('isCurrent')
			}
		}
		// Set progress visibility
		setIsProgressVisible(scrollY > wh.current * 0.5 && scrollY < wh.current * 0.1 + wh.current * 5)
	}

	/**
	*	Expand sections text
	*/
	const sectionTextEls = useRef([])
	const sectionTextElsHeights = useRef([])

	useEffect(() => {
		const ww = window.innerWidth
		sectionTextElsHeights.current = sectionTextEls.current.map(el => 100 * (el.offsetHeight / ww))
		sectionTextEls.current.forEach(el => {
			el.style.height = 0
		})
	}, [])

	const handlePlusButtonClick = e => {
		const parentSection = e.target.closest('.Technology__Section')
		const index = parseInt(parentSection.getAttribute('data-section-index'))
		if (parentSection.classList.contains('isExpanded')) {
			sectionTextEls.current[index].style.height = 0
		} else {
			sectionTextEls.current[index].style.height = sectionTextElsHeights.current[index] + 'vw'
		}
		parentSection.classList.toggle('isExpanded')
	}

	/**
	*	Technologies list modal
	*/
	const [isModalOpen, setIsModalOpen] = useState(false)
	const defaultModalContent = {
		background: null,
		logo: null,
		name: null,
		subtitle: null,
		content: null
	}
	const [modalContent, setModalContent] = useState(defaultModalContent)

	const openModal = data => {
		const newModalContent = {
			backgroundImage: data.background_image,
			logo: data.logo,
			name: data.name,
			subtitle: data.subtitle,
			content: data.content
		}
		setModalContent(newModalContent)
		setIsModalOpen(true)
	}

  return (
    <main className="Technology">
			<Seo
				title="Technology"
				description={pageData.meta_description.text}
				url={location.href}
			/>
			<img src={ColorWitnessImage} className="ColorWitnessImageTech" />
			<div className="Technology__FixedSectionsContainer" data-menu-color="lWhite,mGrey,sWhite">
				<div className="Technology__Hero">
					<img
						className="Technology__HeroBackground"
						src={pageData.hero_background_image.fluid.src}
						srcSet={pageData.hero_background_image.fluid.srcSet}
						alt=""
						ref={heroBackgroundEl}
					/>
					<img
						className="Technology__HeroBackground Technology__HeroBackground--Mobile"
						src={pageData.hero_background_image_mobile.fluid.src}
						srcSet={pageData.hero_background_image_mobile.fluid.srcSet}
						alt=""
					/>
					<h1 className="Technology__Title h1 enterByLetters" data-is-on-screen>
						<SplitText splitBy="chars">
							{pageData.title.text}
						</SplitText>
					</h1>
					<ul className="Technology__Icons">
						<li className="Technology__Icon">
							<div className="Technology__Link" onClick={() => smoothScrollTo(document.querySelector('#section1'))}>
								<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="28.964" cy="28.882" r="28.007" stroke="#EAE7DD"/><ellipse cx="29.5" cy="23.581" rx="6.5" ry="2.581" stroke="#EAE7DD"/><path d="M36 34.42c0 1.424-2.91 2.58-6.5 2.58S23 35.844 23 34.42M23 23.58v10.84M36 23.58v10.84" stroke="#EAE7DD"/></svg>
							</div>
						</li>
						<li className="Technology__Icon">
							<div className="Technology__Link" onClick={() => smoothScrollTo(document.querySelector('#section2'))}>
								<svg viewBox="0 0 58 58"><g transform="translate(1 1)" fill="none" fillRule="evenodd"><circle stroke="#EAE7DD" opacity=".3" cx="28.007" cy="28.007" r="28.007"/><g transform="translate(22 20)"><path d="M.055 0c-1 6.062 12 10.151 11 16" stroke="#EAE7DD" strokeLinecap="round"/><circle fill="#EAE7DD" fillRule="nonzero" cx="5.555" cy="15.5" r="1"/><circle fill="#EAE7DD" fillRule="nonzero" cx="5.555" cy="1" r="1"/><path d="M11.055 0c1 6.062-12 10.151-11 16" stroke="#EAE7DD" strokeLinecap="round"/></g></g></svg>
							</div>
						</li>
						<li className="Technology__Icon">
							<div className="Technology__Link" onClick={() => smoothScrollTo(document.querySelector('#section3'))}>
								<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="28.999" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="M36 31.135C36 33.872 33.5 37 28.933 37 24.5 37 22 34.233 22 31.135c0-4.3 2.692-6.965 2.692-6.965-1.446 2.053 2.465 5.259 2.465 5.259C24.357 23.564 32.391 19 32.391 19 30.488 23.988 36 26.443 36 31.135Z" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
						</li>
						<li className="Technology__Icon">
							<div className="Technology__Link" onClick={() => smoothScrollTo(document.querySelector('#section4'))}>
								<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="29.007" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="M37.404 20.99a4.67 4.67 0 0 0-6.605 0c-2.642 2.642-7.431 11.835-9.495 16.101 4.266-2.064 13.458-6.853 16.1-9.495a4.67 4.67 0 0 0 0-6.606Z" stroke="#EAE7DD"/><circle cx="34" cy="24" r="1" fill="#EAE7DD"/></svg>
							</div>
						</li>
						<li className="Technology__Icon">
							<div className="Technology__Link" onClick={() => smoothScrollTo(document.querySelector('#section5'))}>
								<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="29.007" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="m20 28.5 9 5 9-5" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/><path d="m29 29.5 9-5-9-4.5-9 4.5 9 5ZM20 32.5l9 5 9-5" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
						</li>
					</ul>
					<div className="Technology__ScrollCta">
					</div>
				</div>
				<section className="Technology__Section" id="section1" data-section-index={0}>
					<div className="Technology__SectionOverlay" ref={ref => sectionsOverlays.current[0] = ref}></div>
					<div className="Technology__SectionInfos">
						{pageData.first_slide_image && <img
							className="Technology__SectionBackground"
							src={pageData.first_slide_image.fluid.src}
							src={pageData.first_slide_image.fluid.srcSet}
							alt=""
							ref={ref => sectionsBackgroundsEls.current[0] = ref}
						/>}
						<h2 className="Technology__SectionTitle h1 enterByLetters" data-is-on-screen>
							<SplitText splitBy="chars">
								{pageData.first_slide_title.text}
							</SplitText>
						</h2>
						<div className="Technology__SectionText bodySans" ref={ref => sectionTextEls.current[0] = ref}>
							{pageData.first_slide_text.text}
						</div>
						<div className="Technology__PlusButton" onClick={handlePlusButtonClick}></div>
					</div>
					<div className="Technology__SectionVideoContainer">
						<VideoResponsive
							className="Technology__SectionVideo"
							srcDesktop={video1}
							srcMobile={video1}
							autoPlay={false}
							loop={true}
							playsInline={true}
							isPlaying={videosArePlaying[0]}
						/>
					</div>
				</section>
				<section className="Technology__Section" id="section2" data-section-index={1}>
					<div className="Technology__SectionOverlay" ref={ref => sectionsOverlays.current[1] = ref}></div>
					<div className="Technology__SectionInfos">
						{pageData.second_slide_image && <img
							className="Technology__SectionBackground"
							src={pageData.second_slide_image.fluid.src}
							src={pageData.second_slide_image.fluid.srcSet}
							alt=""
							ref={ref => sectionsBackgroundsEls.current[1] = ref}
						/>}
						<h2 className="Technology__SectionTitle h1 enterByLetters" data-is-on-screen>
							<SplitText splitBy="chars">
								{pageData.second_slide_title.text}
							</SplitText>
						</h2>
						<div className="Technology__SectionText bodySans" ref={ref => sectionTextEls.current[1] = ref}>
							{pageData.second_slide_text.text}
						</div>
						<div className="Technology__PlusButton" onClick={handlePlusButtonClick}></div>
					</div>
					<div className="Technology__SectionVideoContainer">
						<VideoResponsive
							className="Technology__SectionVideo"
							srcDesktop={video2}
							srcMobile={video2}
							autoPlay={false}
							loop={true}
							playsInline={true}
							isPlaying={videosArePlaying[1]}
						/>
					</div>
				</section>
				<section className="Technology__Section"  id="section3" data-section-index={2}>
					<div className="Technology__SectionOverlay" ref={ref => sectionsOverlays.current[2] = ref}></div>
					<div className="Technology__SectionInfos">
						{pageData.third_slide_image && <img
							className="Technology__SectionBackground"
							src={pageData.third_slide_image.fluid.src}
							src={pageData.third_slide_image.fluid.srcSet}
							alt=""
							ref={ref => sectionsBackgroundsEls.current[2] = ref}
						/>}
						<h2 className="Technology__SectionTitle h1 enterByLetters" data-is-on-screen>
							<SplitText splitBy="chars">
								{pageData.third_slide_title.text}
							</SplitText>
						</h2>
						<div className="Technology__SectionText bodySans" ref={ref => sectionTextEls.current[2] = ref}>
							{pageData.third_slide_text.text}
						</div>
						<div className="Technology__PlusButton" onClick={handlePlusButtonClick}></div>
					</div>
					<div className="Technology__SectionVideoContainer">
						<VideoResponsive
							className="Technology__SectionVideo"
							srcDesktop={video3}
							srcMobile={video3}
							autoPlay={false}
							loop={true}
							playsInline={true}
							isPlaying={videosArePlaying[2]}
						/>
					</div>
				</section>
				<section className="Technology__Section"  id="section4" data-section-index={3}>
					<div className="Technology__SectionOverlay" ref={ref => sectionsOverlays.current[3] = ref}></div>
					<div className="Technology__SectionInfos">
						{pageData.fourth_slide_image && <img
							className="Technology__SectionBackground"
							src={pageData.fourth_slide_image.fluid.src}
							src={pageData.fourth_slide_image.fluid.srcSet}
							alt=""
							ref={ref => sectionsBackgroundsEls.current[3] = ref}
						/>}
						<h2 className="Technology__SectionTitle h1 enterByLetters" data-is-on-screen>
							<SplitText splitBy="chars">
								{pageData.fourth_slide_title.text}
							</SplitText>
						</h2>
						<div className="Technology__SectionText bodySans" ref={ref => sectionTextEls.current[3] = ref}>
							{pageData.fourth_slide_text.text}
						</div>
						<div className="Technology__PlusButton" onClick={handlePlusButtonClick}></div>
					</div>
					<div className="Technology__SectionVideoContainer">
						<VideoResponsive
							className="Technology__SectionVideo"
							srcDesktop={video4}
							srcMobile={video4}
							autoPlay={false}
							loop={true}
							playsInline={true}
							isPlaying={videosArePlaying[3]}
						/>
					</div>
				</section>
				<section className="Technology__Section"  id="section5" data-section-index={4}>
					<div className="Technology__SectionOverlay" ref={ref => sectionsOverlays.current[4] = ref}></div>
					<div className="Technology__SectionInfos">
						{pageData.fifth_slide_image && <img
							className="Technology__SectionBackground"
							src={pageData.fifth_slide_image.fluid.src}
							src={pageData.fifth_slide_image.fluid.srcSet}
							alt=""
							ref={ref => sectionsBackgroundsEls.current[4] = ref}
						/>}
						<h2 className="Technology__SectionTitle h1 enterByLetters" data-is-on-screen>
							<SplitText splitBy="chars">
								{pageData.fifth_slide_title.text}
							</SplitText>
						</h2>
						<div className="Technology__SectionText bodySans" ref={ref => sectionTextEls.current[4] = ref}>
							{pageData.fifth_slide_text.text}
						</div>
						<div className="Technology__PlusButton" onClick={handlePlusButtonClick}></div>
					</div>
					<div className="Technology__SectionVideoContainer">
						<VideoResponsive
							className="Technology__SectionVideo"
							srcDesktop={video5}
							srcMobile={video5}
							autoPlay={false}
							loop={true}
							playsInline={true}
							isPlaying={videosArePlaying[4]}
						/>
					</div>
				</section>
				<ul className={`Technology__Progress ${isProgressVisible ? 'isVisible' : ''}`}>
					<li
						className="Technology__ProgressItem"
						ref={ref => progressItemEls.current[0] = ref}
						onClick={() => smoothScrollTo(null, null, window.innerHeight * 1)}
					>
							<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="28.964" cy="28.882" r="28.007" stroke="#EAE7DD"/><ellipse cx="29.5" cy="23.581" rx="6.5" ry="2.581" stroke="#EAE7DD"/><path d="M36 34.42c0 1.424-2.91 2.58-6.5 2.58S23 35.844 23 34.42M23 23.58v10.84M36 23.58v10.84" stroke="#EAE7DD"/></svg>
					</li>
					<li
						className="Technology__ProgressItem"
						ref={ref => progressItemEls.current[1] = ref}
						onClick={() => smoothScrollTo(null, null, window.innerHeight * 2)}
					>
							<svg viewBox="0 0 58 58"><g transform="translate(1 1)" fill="none" fillRule="evenodd"><circle stroke="#EAE7DD" opacity=".3" cx="28.007" cy="28.007" r="28.007"/><g transform="translate(22 20)"><path d="M.055 0c-1 6.062 12 10.151 11 16" stroke="#EAE7DD" strokeLinecap="round"/><circle fill="#EAE7DD" fillRule="nonzero" cx="5.555" cy="15.5" r="1"/><circle fill="#EAE7DD" fillRule="nonzero" cx="5.555" cy="1" r="1"/><path d="M11.055 0c1 6.062-12 10.151-11 16" stroke="#EAE7DD" strokeLinecap="round"/></g></g></svg>
					</li>
					<li
						className="Technology__ProgressItem"
						ref={ref => progressItemEls.current[2] = ref}
						onClick={() => smoothScrollTo(null, null, window.innerHeight * 3)}
					>
							<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="28.999" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="M36 31.135C36 33.872 33.5 37 28.933 37 24.5 37 22 34.233 22 31.135c0-4.3 2.692-6.965 2.692-6.965-1.446 2.053 2.465 5.259 2.465 5.259C24.357 23.564 32.391 19 32.391 19 30.488 23.988 36 26.443 36 31.135Z" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/></svg>
					</li>
					<li
						className="Technology__ProgressItem"
						ref={ref => progressItemEls.current[3] = ref}
						onClick={() => smoothScrollTo(null, null, window.innerHeight * 4)}
					>
							<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="29.007" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="M37.404 20.99a4.67 4.67 0 0 0-6.605 0c-2.642 2.642-7.431 11.835-9.495 16.101 4.266-2.064 13.458-6.853 16.1-9.495a4.67 4.67 0 0 0 0-6.606Z" stroke="#EAE7DD"/><circle cx="34" cy="24" r="1" fill="#EAE7DD"/></svg>
					</li>
					<li
						className="Technology__ProgressItem"
						ref={ref => progressItemEls.current[4] = ref}
						onClick={() => smoothScrollTo(null, null, window.innerHeight * 5)}
					>
							<svg viewBox="0 0 58 58" fill="none"><circle opacity=".3" cx="29.007" cy="29.007" r="28.007" stroke="#EAE7DD"/><path d="m20 28.5 9 5 9-5" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/><path d="m29 29.5 9-5-9-4.5-9 4.5 9 5ZM20 32.5l9 5 9-5" stroke="#EAE7DD" strokeLinecap="round" strokeLinejoin="round"/></svg>
					</li>
				</ul>
			</div>
			<div className="Technology__List" data-menu-color="lBlack,mGrey,sGrey">
				{pageData.technologies.map((technology, index) => (
					<div
						className="Technology__ListItem"
						key={`technology-${index}`}
						onClick={() => openModal(technology)}
					>
						{technology.background_image && technology.background_image.thumbnails.Card && <img
							className="Technology__ListItemBg"
							src={technology.background_image.thumbnails.Card.fluid.src}
							srcSet={technology.background_image.thumbnails.Card.fluid.srcSet}
							alt={technology.background_image.thumbnails.Card.alt}
						/>}
						{technology.logo && <img
							className="Technology__ListItemLogo"
							src={technology.logo.fluid.src}
							srcSet={technology.logo.fluid.srcSet}
							alt={technology.logo.alt}
						/>}
					</div>
				))}
			</div>
			<section className="Technology__LastSection" data-menu-color="lBlack,mWhite,sWhite">
				<div className="Technology__LastSectionInfos">
					<h5 className="Technology__LastSectionCatchphrase h3">
						{pageData.last_section_catchphrase.text}
					</h5>
					<Button
						className="Technology__LastSectionInfosButton"
						label={pageData.last_section_button_label.text}
						link={`/${pageData.last_section_button_link.type}`}
						colorText="#EAE7DD"
						colorBackground="#9E9A8E"
						colorBorder="none"
					/>
				</div>
				{pageData.last_section_button_image && <img
					className="Technology__LastSectionImage"
					src={pageData.last_section_button_image.fluid.src}
					srcSet={pageData.last_section_button_image.fluid.srcSet}
				/>}
			</section>
			<ModalSide
				isOpen={isModalOpen}
				className="Technology__Modal"
				onClose={() => setIsModalOpen(false)}
			>
				<div className="Technology__ModalHeader">
					{modalContent.backgroundImage &&
						<img
							className="Technology__ModalBackground"
							src={modalContent.backgroundImage.fluid.src}
							srcSet={modalContent.backgroundImage.fluid.srcSet}
							alt={modalContent.backgroundImage.fluid.alt}
						/>
					}
					{modalContent.logo &&
						<img
							className="Technology__ModalLogo"
							src={modalContent.logo.fluid.src}
							srcSet={modalContent.logo.fluid.srcSet}
							alt={modalContent.logo.alt}
						/>
					}
					{/* <div className="Technology__ModalName h3">
						{modalContent.name && modalContent.name.text}
					</div> */}
					<div className="Technology__ModalSubtitle capsSmall">
						{modalContent.subtitle && modalContent.subtitle.text}
					</div>
				</div>
				<div className="Technology__ModalContent bodySansSmall">
					{modalContent.content &&
						<RichText render={modalContent.content.raw}/>
					}
				</div>
			</ModalSide>
    </main>
  )
}

export default Technology

export const query = graphql`
	query Technology {
		prismicTechnology {
			data {
				meta_description {
					text
				}
				title {
					text
				}
				hero_background_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				hero_background_image_mobile {
					alt
					fluid {
						src
						srcSet
					}
				}
				first_slide_title {
					text
				}
				first_slide_text {
					text
				}
				first_slide_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				second_slide_title {
					text
				}
				second_slide_text {
					text
				}
				second_slide_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				third_slide_title {
					text
				}
				third_slide_text {
					text
				}
				third_slide_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				fourth_slide_title {
					text
				}
				fourth_slide_text {
					text
				}
				fourth_slide_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				fifth_slide_title {
					text
				}
				fifth_slide_text {
					text
				}
				fifth_slide_image {
					alt
					fluid {
						src
						srcSet
					}
				}
				technologies {
					name {
						text
					}
					subtitle {
						text
					}
					logo {
						alt
						fluid {
							src
							srcSet
						}
					}
					background_image {
						alt
						fluid {
							src
							srcSet
						}
						thumbnails {
							Card {
								alt
								fluid {
									src
									srcSet
								}
							}
						}
					}
					content {
						raw
					}
				}
				last_section_catchphrase {
					text
				}
				last_section_button_label {
					text
				}
				last_section_button_link {
					type
				}
				last_section_button_image {
					alt
					fluid {
						src
						srcSet
					}
				}
			}
		}
		prismicBannerAndPopup {
			...BannerAndPopup
			...CookiesNotice
		}
		prismicNewsletter {
			...Newsletter
		}
		prismicFooter {
			...Footer
		}
	}
`